import React from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { getMochiMeter, METER_LEVELS } from "@/lib/mochiMeter";

// Compact "Hype Meter" pill — shows current level (Spark / Flame / Fire /
// Blaze / Inferno) with a thin progress sliver underneath. Designed to sit
// alongside the Feed button on the dashboard.
//
//   <MochiMeterPill meterValue={settings.meter_value} />
export function MochiMeterPill({ meterValue = 0, animateLevelUp = false, onIgnite }) {
    const meter = getMochiMeter(meterValue);
    const { current, progress, capped } = meter;
    // Flame icon size grows with level
    const iconSize = 12 + current.level * 2; // 14 → 22

    const clickable = capped && typeof onIgnite === "function";
    const Comp = clickable ? motion.button : motion.div;
    const interactiveProps = clickable
        ? {
              type: "button",
              onClick: onIgnite,
              whileHover: { scale: 1.06 },
              whileTap: { scale: 0.94 },
              "aria-label": "Trigger Mochi's fire dance",
              title: "Tap for fire dance 🔥",
              className: `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full shadow-clay ${current.tint} cursor-pointer ring-2 ring-mustard/60 hover:ring-mustard transition-all`,
          }
        : {
              "aria-label": `Hunger level: ${current.name}`,
              title: capped ? "Stuffed (max)" : `Lv ${current.level} · ${current.name}`,
              className: `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full shadow-clay ${current.tint}`,
          };

    return (
        <Comp
            initial={false}
            animate={
                animateLevelUp
                    ? { scale: [1, 1.18, 1], rotate: [0, -6, 6, 0] }
                    : clickable
                    ? {
                          // At Inferno: a slow breathing pulse hints the pill is interactive.
                          boxShadow: [
                              "0 4px 14px rgba(217,108,84,0.25)",
                              "0 6px 22px rgba(217,108,84,0.55)",
                              "0 4px 14px rgba(217,108,84,0.25)",
                          ],
                      }
                    : { scale: 1 }
            }
            transition={
                animateLevelUp
                    ? { duration: 0.5, ease: "easeOut" }
                    : clickable
                    ? { duration: 2.0, repeat: Infinity, ease: "easeInOut" }
                    : { duration: 0.5, ease: "easeOut" }
            }
            data-testid="mochi-meter-pill"
            {...interactiveProps}
        >
            <div className="flex items-center gap-1.5">
                <motion.div
                    animate={current.level >= 4
                        ? { scale: [1, 1.18, 1], rotate: [0, -8, 8, 0] }
                        : current.level >= 2
                        ? { y: [0, -1.5, 0] }
                        : undefined}
                    transition={{ duration: current.level >= 4 ? 0.9 : 2.4, repeat: Infinity, ease: "easeInOut" }}
                    aria-hidden
                >
                    <Flame size={iconSize} className={`${current.fg} ${capped ? "drop-shadow-[0_0_6px_rgba(217,108,84,0.7)]" : ""}`} fill={current.level >= 3 ? "currentColor" : "none"} />
                </motion.div>
                <span className={`font-display text-xs leading-none ${capped ? current.fg : "text-warm-900"}`} data-testid="mochi-meter-name">
                    {current.name}
                </span>
            </div>
            {/* Thin progress sliver — full at Inferno */}
            <div className="w-full h-1 rounded-full bg-cream/60 overflow-hidden mt-0.5">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.round(progress * 100)}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="h-full bg-warm-900/40 rounded-full"
                    data-testid="mochi-meter-pill-bar"
                />
            </div>
        </Comp>
    );
}

// Bigger 5-segment meter display for the Stats page. Each segment lights up
// as the meter passes its threshold (purely visual — meter drains over 48h
// since the last feed).
//
//   <MochiMeterStrip meterValue={settings.meter_value} />
export function MochiMeterStrip({ meterValue = 0 }) {
    const meter = getMochiMeter(meterValue);
    const { current, next, toNext, capped, value } = meter;
    return (
        <div data-testid="mochi-meter-strip">
            <div className="flex items-end justify-between text-[10px] font-display uppercase tracking-widest text-warm-500 mb-1.5">
                <span>Mochi hunger meter</span>
                <span data-testid="mochi-meter-strip-current">
                    {current.name}
                    {capped ? " · max" : ` · lv ${current.level}/5`}
                </span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
                {METER_LEVELS.map((lv) => {
                    const reached = value >= lv.minValue;
                    const isCurrent = lv.level === current.level;
                    return (
                        <div
                            key={lv.level}
                            className={`relative h-9 rounded-xl flex items-center justify-center transition-all ${
                                reached ? lv.tint : "bg-surface"
                            } ${isCurrent ? "ring-2 ring-terracotta/60" : ""}`}
                            data-testid={`mochi-meter-segment-${lv.level}`}
                        >
                            <Flame
                                size={14 + lv.level * 1.5}
                                className={reached ? lv.fg : "text-warm-500/40"}
                                fill={reached && lv.level >= 3 ? "currentColor" : "none"}
                            />
                            <span
                                className={`absolute -bottom-4 left-0 right-0 text-center font-display text-[9px] uppercase tracking-wide ${
                                    reached ? "text-warm-900" : "text-warm-500/60"
                                }`}
                            >
                                {lv.name}
                            </span>
                        </div>
                    );
                })}
            </div>
            <p
                className="font-body text-[11px] text-warm-500 mt-6 text-right italic"
                data-testid="mochi-meter-strip-to-next"
            >
                {capped
                    ? "Mochi is fully ignited — fire dance unlocked 🔥"
                    : `${toNext} more ${toNext === 1 ? "rice ball" : "rice balls"} until ${next.name}`}
            </p>
            <p className="font-body text-[10px] text-warm-500/70 mt-1 text-right italic">
                drains over 48h without feeding
            </p>
        </div>
    );
}
